import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../../_services/alert.service';
import { SupplierService } from '../../../_services/supplier.service';
import { Supplier, SupplierPurchaseOrder } from '../../../_interfaces/supplier';
import { COUNTRIES, CountryOption, countryFlag, findCountry } from '../countries';

// Backed by SupplierService (/suppliers/*).
@Component({
    selector: 'app-edit-supplier',
    imports: [
        MainLayoutComponent,
        CommonModule,
        FormsModule,
        RouterLink,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatAutocompleteModule,
    ],
    templateUrl: './edit-supplier.component.html',
    // Same form styles as the purchase order / shipment pages.
    styleUrl: '../../purchases/purchase-form.css'
})
export class EditSupplierComponent implements OnInit {
  isEditMode = false;
  supplierId: number | null = null;
  saving = false;
  loading = false;
  purchaseOrders: SupplierPurchaseOrder[] = [];
  submitted = false;

  name = '';
  email = '';
  phone = '';
  vatNumber = '';
  website = '';
  addressLine1 = '';
  addressLine2 = '';
  city = '';
  postcode = '';
  country = '';
  notes = '';

  // Every supplier, to warn before adding one that already exists.
  private allSuppliers: Supplier[] = [];
  // Set when the purchase order page sent us here to add a supplier, so
  // saving goes back to a new PO with them already picked.
  returnToPo = false;
  countryFlag = countryFlag;

  // One-click starters for the notes box.
  readonly noteStarters = ['Lead time', 'Minimum order', 'Account manager', 'Payment terms', 'Delivery days'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
    private supplierService: SupplierService,
  ) {}

  ngOnInit(): void {
    this.returnToPo = this.route.snapshot.queryParamMap.get('returnTo') === 'po';
    this.supplierService.getSuppliers().subscribe({
      next: (suppliers) => (this.allSuppliers = suppliers),
      // Non-fatal: only the duplicate warning goes quiet.
      error: () => (this.allSuppliers = []),
    });
    // Subscribed rather than read once: "Open it instead" on the duplicate
    // warning goes edit -> edit, which reuses this component instance.
    this.route.paramMap.subscribe((params) => {
      const idParam = params.get('id');
      if (idParam) {
        this.isEditMode = true;
        this.supplierId = Number(idParam);
        this.submitted = false;
        this.loading = true;
        this.loadSupplier(this.supplierId);
      }
    });
  }

  private loadSupplier(id: number): void {
    this.supplierService.getSupplierById(id).subscribe({
      next: (supplier) => {
        this.loading = false;
        this.purchaseOrders = supplier.purchaseOrders ?? [];
        this.name = supplier.name;
        this.email = supplier.email || '';
        this.phone = supplier.phone || '';
        this.vatNumber = supplier.vatNumber || '';
        this.website = supplier.website || '';
        this.addressLine1 = supplier.addressLine1 || '';
        this.addressLine2 = supplier.addressLine2 || '';
        this.city = supplier.city || '';
        this.postcode = supplier.postcode || '';
        this.country = supplier.country || '';
        this.notes = supplier.notes || '';
      },
      error: () => {
        this.loading = false;
        this.alertService.alert('Supplier not found', 'error');
        this.router.navigate(['/suppliers']);
      },
    });
  }

  // Another supplier already saved under the same name.
  get duplicateSupplier(): Supplier | undefined {
    const n = this.name.trim().toLowerCase();
    if (!n) return undefined;
    return this.allSuppliers.find((s) => s.id !== this.supplierId && s.name.trim().toLowerCase() === n);
  }

  get countryMatch(): CountryOption | undefined {
    return findCountry(this.country);
  }

  get filteredCountries(): CountryOption[] {
    const q = this.country.trim().toLowerCase();
    if (!q || this.countryMatch?.name.toLowerCase() === q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase() === q);
  }

  // How complete the record is - the sidebar checklist jumps to each part.
  get checklist(): { label: string; done: boolean; section: string }[] {
    return [
      { label: 'Supplier name', done: !!this.name.trim(), section: 'company' },
      { label: 'Email for sending POs', done: !!this.email.trim() && !this.emailInvalid, section: 'contact' },
      { label: 'Phone number', done: !!this.phone.trim(), section: 'contact' },
      { label: 'Address', done: this.hasAddress, section: 'address' },
      { label: 'Country', done: !!this.country.trim(), section: 'address' },
      { label: 'VAT number', done: !!this.vatNumber.trim(), section: 'company' },
      { label: 'Website', done: !!this.website.trim(), section: 'company' },
    ];
  }

  get completePct(): number {
    const list = this.checklist;
    return Math.round((list.filter((c) => c.done).length / list.length) * 100);
  }

  get saveBlockers(): string[] {
    const blockers: string[] = [];
    if (!this.name.trim()) blockers.push('Add a supplier name');
    if (this.emailInvalid) blockers.push('Fix the email address');
    return blockers;
  }

  scrollTo(section: string): void {
    document.getElementById('sec-' + section)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Adds "Lead time: " etc. on a new line of the notes, ready to finish.
  addNoteStarter(label: string, textarea: HTMLTextAreaElement): void {
    const current = this.notes.replace(/\s+$/, '');
    this.notes = (current ? current + '\n' : '') + label + ': ';
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(this.notes.length, this.notes.length);
    });
  }

  onNewPurchaseOrder(): void {
    this.router.navigate(['/purchases/create'], { queryParams: { supplierId: this.supplierId } });
  }

  get emailInvalid(): boolean {
    return !!this.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.trim());
  }

  get initials(): string {
    const words = (this.name || '?').trim().split(/\s+/);
    return ((words[0]?.[0] ?? '?') + (words[1]?.[0] ?? '')).toUpperCase();
  }

  get openOrders(): number {
    return this.purchaseOrders.filter((o) => !['RECEIVED', 'CANCELLED'].includes(o.status)).length;
  }

  // Ex-tax item value per currency - a supplier can be ordered from in more than one.
  get spendByCurrency(): { currency: string; total: number }[] {
    const totals = new Map<string, number>();
    for (const o of this.purchaseOrders) {
      if (o.status === 'CANCELLED') continue;
      totals.set(o.currency, (totals.get(o.currency) ?? 0) + Number(o.itemsTotal || 0));
    }
    return [...totals].map(([currency, total]) => ({ currency, total }));
  }

  get mapsUrl(): string {
    const q = [this.addressLine1, this.addressLine2, this.city, this.postcode, this.country].filter(Boolean).join(', ');
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q);
  }

  get hasAddress(): boolean {
    return !!(this.addressLine1 || this.city || this.postcode);
  }

  get addressSummary(): string {
    return [this.addressLine1, this.city, this.postcode].filter(Boolean).join(', ');
  }

  get websiteUrl(): string {
    const w = this.website.trim();
    return /^https?:\/\//i.test(w) ? w : `https://${w}`;
  }

  statusClass(status: string): string {
    switch (status) {
      case 'RECEIVED': return 'bg-emerald-100 text-emerald-800';
      case 'PARTIALLY RECEIVED': return 'bg-amber-100 text-amber-800';
      case 'ORDERED': return 'bg-sky-100 text-sky-800';
      case 'CANCELLED': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  }

  billingClass(status: string): string {
    switch (status) {
      case 'PAID': return 'bg-emerald-50 text-emerald-700';
      case 'BILLED': return 'bg-sky-50 text-sky-700';
      case 'OVERDUE': return 'bg-red-50 text-red-700';
      default: return 'bg-gray-50 text-gray-500';
    }
  }

  onOpenOrder(order: SupplierPurchaseOrder): void {
    this.router.navigate(['/purchases/edit', order.id]);
  }

  onSave(): void {
    this.submitted = true;
    if (!this.name.trim()) {
      this.alertService.alert('Supplier name is required', 'error');
      return;
    }
    if (this.emailInvalid) {
      this.alertService.alert('Please enter a valid email address', 'error');
      return;
    }

    const input = {
      name: this.name,
      email: this.email || undefined,
      phone: this.phone || undefined,
      vatNumber: this.vatNumber || undefined,
      website: this.website || undefined,
      addressLine1: this.addressLine1 || undefined,
      addressLine2: this.addressLine2 || undefined,
      city: this.city || undefined,
      postcode: this.postcode || undefined,
      country: this.country || undefined,
      notes: this.notes || undefined,
    };

    this.saving = true;
    const request: Observable<unknown> = this.isEditMode
      ? this.supplierService.updateSupplier(this.supplierId!, input)
      : this.supplierService.createSupplier(input);

    request.subscribe({
      next: (saved) => {
        this.saving = false;
        this.alertService.alert(this.isEditMode ? 'Supplier updated' : 'Supplier created', 'success');
        const newId = (saved as Supplier | null)?.id;
        if (this.returnToPo && !this.isEditMode && newId) {
          this.router.navigate(['/purchases/create'], { queryParams: { supplierId: newId } });
        } else {
          this.router.navigate(['/suppliers']);
        }
      },
      error: (error: any) => {
        this.saving = false;
        this.alertService.alert(error?.error?.message || 'Could not save supplier', 'error');
      },
    });
  }

  onCancel(): void {
    this.router.navigate(this.returnToPo ? ['/purchases/create'] : ['/suppliers']);
  }
}
